/**
 * B2BLayout — Phase 33 (Multi-Tenant: Branch Switcher)
 *
 * Changes in Phase 33:
 *   • Branch Switcher dropdown added to the header (visible for 'client' role
 *     when the user has more than one branch / supplier record).
 *   • Shows the active branch name in the header pill.
 *   • resolveCtx() now uses the cart item's product.company_id as company_id
 *     and looks up the matching client record from clientRecords — enabling
 *     correct multi-supplier order submission.
 *   • Cart submission emits one order per distinct company_id when the cart
 *     spans multiple suppliers (multi-vendor cart split).
 *
 * Phase 16 (original):
 *   • App shell for all authenticated /b2b/* pages.
 *   • Agent "Acting as" banner with Switch Client.
 *   • Cart drawer with order submission.
 */

import { useState, useCallback, useRef, useEffect } from 'react';
import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { ShoppingBag, LayoutDashboard, BookOpen, ClipboardList, ClipboardCheck, Tags, X, Trash2, LogOut, ShoppingCart, Plus, Minus, Loader as Loader2, CircleCheck as CheckCircle2, CircleAlert as AlertCircle, Briefcase, Users, ArrowLeftRight, ChevronDown, Building2, GitBranch, Truck, FileCheck as FileCheck2, RotateCcw, Package, Warehouse, Circle as HelpCircle, RefreshCw, Maximize2, FileText } from 'lucide-react';
import B2BExpandedCart from './B2BExpandedCart';
import { supabase }        from '@/lib/supabase';
import { useAuth }         from '@/features/auth/AuthContext';
import {
  submitOrder,
  classifyAuthenticatedUser,
  type B2BOrderType,
} from '@/lib/b2bMarketplaceService';
import { B2BCartProvider, useB2BCart } from './B2BCartContext';
import { B2BTermsProvider, useB2BTerms } from './B2BTermsContext';
import {
  useB2BUser,
  type AgentClientRecord,
  type AgentCompanyOption,
  type B2BUserSession,
} from './B2BUserContext';

// ─── Toast ───────────────────────────────────────────────────────────────────

type ToastKind = 'success' | 'error';
interface ToastState { kind: ToastKind; message: string; }

// ─── Branch Switcher Dropdown ─────────────────────────────────────────────────
// Shown in the header for 'client' role when clientRecords has ≥ 1 entry.

interface BranchSwitcherProps {
  clientRecords:    AgentClientRecord[];
  activeBranchId:   string | null;
  onSelect:         (record: AgentClientRecord) => void;
  supplierNames:    Record<string, string>;   // company_id → legal_name
  /** Override the dropdown header label (default: 'החלפת סניף / עסק') */
  dropdownLabel?:   string;
  /** Override the trigger button placeholder text when nothing is selected */
  placeholder?:     string;
}

function BranchSwitcher({
  clientRecords,
  activeBranchId,
  onSelect,
  supplierNames,
  dropdownLabel = 'החלפת סניף / עסק',
  placeholder   = 'בחר סניף',
}: BranchSwitcherProps) {
  const [open, setOpen] = useState(false);
  const ref             = useRef<HTMLDivElement>(null);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const activeRecord = clientRecords.find(r => r.id === activeBranchId);
  const activeName   = activeRecord?.name ?? placeholder;
  const supplierName = activeRecord ? (supplierNames[activeRecord.company_id] ?? '') : '';
  const activeBusinessId = activeRecord?.business_id;

  // Phase 34: Group by business_id when available, falling back to company_id.
  // Records with the same business_id (ח.פ.) are merged under one group label
  // so the user sees "עסק: 514123456" rather than separate per-company entries.
  type GroupKey = string; // business_id ?? company_id
  const grouped: Record<GroupKey, { label: string; records: AgentClientRecord[] }> = {};
  for (const r of clientRecords) {
    const key = r.business_id ?? r.company_id;
    if (!grouped[key]) {
      grouped[key] = {
        label: r.business_id
          ? `ח.פ. ${r.business_id}`
          : (supplierNames[r.company_id] ?? 'ספק'),
        records: [],
      };
    }
    grouped[key]!.records.push(r);
  }

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen(o => !o)}
        className={[
          'flex items-center gap-1.5 px-2.5 py-1.5 rounded-xl border text-xs font-semibold',
          'transition-colors focus:outline-none focus:ring-2 focus:ring-primary-300',
          open
            ? 'bg-primary-50 border-primary-300 text-primary-700'
            : 'bg-white border-gray-200 text-gray-700 hover:border-primary-300 hover:text-primary-700',
        ].join(' ')}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <GitBranch className="w-3.5 h-3.5 shrink-0" />
        <span className="max-w-[70px] sm:max-w-[100px] truncate">{activeName}</span>
        {(activeBusinessId || supplierName) && (
          <span className="text-[10px] font-normal text-gray-400 hidden sm:block truncate max-w-[90px]">
            · {activeBusinessId ? `ח.פ. ${activeBusinessId}` : supplierName}
          </span>
        )}
        <ChevronDown className={`w-3 h-3 shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div
          className="absolute top-full mt-1 right-0 z-50 min-w-[240px] max-w-xs
                     bg-white border border-gray-200 rounded-2xl shadow-xl overflow-hidden"
          role="listbox"
        >
          <div className="px-3 pt-3 pb-1.5 border-b border-gray-100">
            <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide">
              {dropdownLabel}
            </p>
          </div>

          <div className="max-h-64 overflow-y-auto py-1">
            {Object.entries(grouped).map(([groupKey, { label, records }]) => (
              <div key={groupKey}>
                {/* Group header (business_id or supplier name) */}
                <div className="flex items-center gap-1.5 px-3 py-1.5 mt-1">
                  <Building2 className="w-3 h-3 text-gray-300 shrink-0" />
                  <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide truncate">
                    {label}
                  </span>
                </div>

                {records.map(record => {
                  const isActive = record.id === activeBranchId;
                  return (
                    <button
                      key={record.id}
                      role="option"
                      aria-selected={isActive}
                      onClick={() => { onSelect(record); setOpen(false); }}
                      className={[
                        'w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-right transition-colors',
                        isActive
                          ? 'bg-primary-50 text-primary-700 font-semibold'
                          : 'text-gray-700 hover:bg-gray-50',
                      ].join(' ')}
                    >
                      <GitBranch className="w-3.5 h-3.5 shrink-0 text-gray-300" />
                      <div className="flex-1 min-w-0 text-right">
                        <p className="truncate">{record.name}</p>
                        {record.business_id && (
                          <p className="text-[10px] text-gray-400 font-mono" dir="ltr">
                            {supplierNames[record.company_id] ?? ''}
                          </p>
                        )}
                      </div>
                      {isActive && <CheckCircle2 className="w-3.5 h-3.5 text-primary-500 shrink-0" />}
                    </button>
                  );
                })}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Phase 74 (74.1 robustness): Company Switcher Dropdown ───────────────────
// Phase 74.1 change: ALWAYS rendered for agents — even when availableCompanies
// is currently empty or has only 1 entry — so the agent can visually confirm
// the component is mounted.  When the list is loading or has 0/1 entries we
// render a disabled state with an explanatory label ('טוען ספקים…' or
// 'ספק יחיד · לא נדרש מתג').  This eliminates the "silent missing dropdown"
// failure mode where the strict `> 1` gate hid the UI before the RPC data
// reached React state (cache races, RLS hiccups, RPC unapplied, etc.).

interface CompanySwitcherProps {
  companies:        AgentCompanyOption[];
  activeCompanyId:  string | null;
  onSelect:         (companyId: string) => void;
  /** Phase 74.1 — disable the trigger and show a "loading" label */
  loading?:         boolean;
  /** Phase 74.1 — disable interaction (but still render) when only 0/1 company */
  disabled?:        boolean;
  /** Phase 74.1 — manual refresh callback shown in the dropdown footer */
  onRefresh?:       () => void;
}

function CompanySwitcher({
  companies,
  activeCompanyId,
  onSelect,
  loading,
  disabled,
  onRefresh,
}: CompanySwitcherProps) {
  const [open, setOpen] = useState(false);
  const ref             = useRef<HTMLDivElement>(null);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const active     = companies.find(c => c.company_id === activeCompanyId);
  // Phase 74.1: distinct labels for each state so the user always knows
  // why the switcher might not be functional in this moment.
  const triggerLabel = loading
    ? 'טוען ספקים…'
    : (companies.length === 0
        ? 'אין ספקים זמינים'
        : (active?.company_name?.trim()
            || (companies.length === 1
                  ? (companies[0]?.company_name?.trim() || '(ספק ללא שם)')
                  : 'בחר ספק / חברה')));

  // Trigger is interactive only when we actually have something to switch to.
  const isInteractive = !loading && !disabled && companies.length > 1;

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => {
          if (!isInteractive) {
            // Allow opening the dropdown to surface diagnostic info even
            // when there's nothing to switch to (single-company / loading
            // / empty) — but only when not in the loading state, so the
            // user can also trigger a manual refresh.
            if (!loading) setOpen(o => !o);
            return;
          }
          setOpen(o => !o);
        }}
        className={[
          'flex items-center gap-1.5 px-2.5 py-1.5 rounded-xl border text-xs font-semibold',
          'transition-colors focus:outline-none focus:ring-2 focus:ring-orange-300',
          open
            ? 'bg-orange-50 border-orange-300 text-orange-700'
            : isInteractive
              ? 'bg-white border-orange-200 text-orange-700 hover:border-orange-300'
              : 'bg-gray-50 border-gray-200 text-gray-500 cursor-help',
        ].join(' ')}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-disabled={!isInteractive}
        title={isInteractive
          ? 'בחר ספק / חברה'
          : (loading
              ? 'טוען רשימת ספקים…'
              : 'הסוכן רשום אצל ספק יחיד — לא נדרש מתג החלפה')}
      >
        {loading
          ? <Loader2 className="w-3.5 h-3.5 shrink-0 animate-spin" />
          : <Building2 className="w-3.5 h-3.5 shrink-0" />}
        <span className="max-w-[80px] sm:max-w-[140px] truncate">{triggerLabel}</span>
        {!loading && (
          <ChevronDown className={`w-3 h-3 shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} />
        )}
      </button>

      {open && !loading && (
        <div
          className="absolute top-full mt-1 right-0 z-50 min-w-[260px] max-w-xs
                     bg-white border border-gray-200 rounded-2xl shadow-xl overflow-hidden"
          role="listbox"
        >
          <div className="px-3 pt-3 pb-1.5 border-b border-gray-100">
            <p className="text-[10px] font-semibold text-orange-600 uppercase tracking-wide">
              בחר ספק / חברה
            </p>
            <p className="text-[10px] text-gray-400 mt-0.5">
              {companies.length > 1
                ? 'החלפת ספק תאפס את הלקוח הפעיל ואת העגלה'
                : companies.length === 1
                  ? 'הסוכן רשום אצל ספק יחיד — לא נדרש מתג החלפה'
                  : 'לא נמצאו ספקים — נסה לרענן או צור קשר עם המנהל'}
            </p>
          </div>

          {companies.length === 0 && (
            <div className="px-4 py-4 text-xs text-gray-500 text-right">
              לא נמצאו ספקים פעילים.<br />
              ייתכן שהמיגרציה <code dir="ltr" className="text-[10px] bg-gray-100 px-1 rounded">phase-74</code> טרם הופעלה,
              או שאין לסוכן רשומה ב־<code dir="ltr" className="text-[10px] bg-gray-100 px-1 rounded">inventory_agents</code>.
            </div>
          )}

          {companies.length > 0 && (
            <div className="max-h-64 overflow-y-auto py-1">
              {companies.map(c => {
                const isActive    = c.company_id === activeCompanyId;
                const clickable   = companies.length > 1;
                return (
                  <button
                    key={c.company_id}
                    role="option"
                    aria-selected={isActive}
                    disabled={!clickable}
                    onClick={() => {
                      if (!clickable) return;
                      onSelect(c.company_id);
                      setOpen(false);
                    }}
                    className={[
                      'w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-right transition-colors',
                      isActive
                        ? 'bg-orange-50 text-orange-700 font-semibold'
                        : clickable
                          ? 'text-gray-700 hover:bg-gray-50'
                          : 'text-gray-500 cursor-default',
                    ].join(' ')}
                  >
                    <Building2 className="w-3.5 h-3.5 shrink-0 text-orange-300" />
                    <span className="flex-1 min-w-0 truncate text-right">
                      {c.company_name?.trim() || '(ספק ללא שם)'}
                    </span>
                    {isActive && <CheckCircle2 className="w-3.5 h-3.5 text-orange-500 shrink-0" />}
                  </button>
                );
              })}
            </div>
          )}

          {/* Phase 74.1: always-on manual refresh link in the footer */}
          {onRefresh && (
            <button
              type="button"
              onClick={() => { onRefresh(); setOpen(false); }}
              className="w-full flex items-center justify-center gap-1.5 px-4 py-2.5
                         text-[11px] font-semibold text-orange-600 hover:bg-orange-50
                         border-t border-gray-100 transition-colors"
            >
              <RefreshCw className="w-3 h-3" />
              רענן רשימת ספקים
            </button>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Inner shell (needs both cart + user context in tree) ────────────────────

function Shell() {
  const { user }    = useAuth();
  const navigate    = useNavigate();
  const location    = useLocation();
  const {
    items, uniqueItems, totalItems, totalPrice,
    setQuantity, removeItem, clearCart,
  } = useB2BCart();
  const {
    session,
    clearActiveClient,
    setActiveBranch,
    seedB2BUser,
    setActiveCompany,
    setAgentCompanies,
  } = useB2BUser();

  const [cartOpen,            setCartOpen]            = useState(false);
  const [cartExpanded,        setCartExpanded]        = useState(false);
  const [submitting,          setSubmitting]          = useState(false);
  const [toast,               setToast]               = useState<ToastState | null>(null);
  const [sessionLoading,      setSessionLoading]      = useState(false);
  // Phase 74.1: independent loading state for the aggressive
  // get_agent_companies_for_jwt fetch — separate from sessionLoading because
  // it can fire AFTER the seed has completed (e.g. when hot-reloading the
  // PostgREST schema cache). Also re-enabled by the manual refresh button.
  const [companiesLoading,    setCompaniesLoading]    = useState(false);
  // Phase 46: Van Sale instant delivery toggle (visible to agents only)
  const [instantDelivery,     setInstantDelivery]     = useState(false);
  // Phase 112: company-level returns toggle
  const [returnsEnabled,      setReturnsEnabled]      = useState(true);
  const { showTerms, remindIfNeeded: remindTermsIfNeeded } = useB2BTerms();

  // ── VAT rate for the active supplier ──────────────────────────────────────
  const [vatRate, setVatRate] = useState<number | null>(null);
  const cartIsVatLiable = session?.activeClientRecord?.is_vat_liable ?? true;

  useEffect(() => {
    const companyId = session?.activeCompanyId;
    if (!companyId) return;

    supabase
      .rpc('get_company_vat_rate', { p_company_id: companyId })
      .then(({ data }) => {
        if (data != null) setVatRate(Number(data));
      });
  }, [session?.activeCompanyId]);

  // ── Phase 112: Fetch b2b_returns_enabled for active company ──────────────
  useEffect(() => {
    const companyId = session?.activeCompanyId;
    if (!companyId) return;
    supabase
      .from('companies')
      .select('b2b_returns_enabled')
      .eq('id', companyId)
      .single()
      .then(({ data }) => {
        if (data) setReturnsEnabled(Boolean((data as { b2b_returns_enabled: boolean }).b2b_returns_enabled));
        else setReturnsEnabled(true);
      });
  }, [session?.activeCompanyId]);

  // ── Phase 73: Order Type (Standard | Consignment) ────────────────────────
  // Footer toggle that is only rendered when the active client has
  // allow_consignment=true. Default is 'standard'. The "double check"
  // confirmation modal below MUST also be displayed for consignment-eligible
  // clients regardless of the footer state, so the user makes a conscious
  // choice every time an order is placed.
  const [orderType, setOrderType] = useState<B2BOrderType>('standard');
  // Map of inventory_clients.id → { allow_consignment, allow_standard_sales }.
  // Cached per session so we don't re-fetch on every cart open.
  const [allowConsignmentByClient, setAllowConsignmentByClient] =
    useState<Record<string, boolean>>({});
  const [allowStandardByClient, setAllowStandardByClient] =
    useState<Record<string, boolean>>({});
  // The "double check" confirmation modal — appears when an order is being
  // submitted on behalf of a consignment-eligible client. Until the user
  // chooses one of the two buttons the order is NOT created.
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);

  // ── Auto-restore session after hard refresh ───────────────────────────────
  // B2BUserContext is in-memory only. After a hard refresh `session` is null
  // even though Supabase still has a valid token. Re-classify the user so the
  // Branch Switcher, Supplier Filter, and product fetch all work correctly.
  //
  // dual_role handling: a user who previously chose a role (agent or client)
  // and then hard-refreshes will be re-classified as dual_role.  We use the
  // current URL path as a hint: /b2b/agent* → restore as agent; otherwise
  // restore as client.  This mirrors the choice they made on the Role Selector.
  useEffect(() => {
    if (session !== null) return;   // already seeded — nothing to do
    if (!user)           return;   // not logged in
    let cancelled = false;
    setSessionLoading(true);
    classifyAuthenticatedUser({ id: user.id, email: user.email, phone: user.phone })
      .then(async classified => {
        if (cancelled) return;

        // Helper: build and seed an agent session from AgentSession data.
        // Phase 74: now async — fetches availableCompanies via the
        // get_agent_companies_for_jwt() RPC so the supplier switcher can
        // appear immediately after a hard refresh.
        const seedAgent = async (
          ag: NonNullable<typeof classified.agentSession>,
        ) => {
          const agentClients = ag.agentClientRecords.map(r => ({
            id:           r.id,
            company_id:   r.company_id,
            name:         r.name,
            email:        r.email,
            phone:        r.phone,
            pricelist_id: r.pricelist_id,
            business_id:  r.business_id ?? null,
            is_vat_liable: r.is_vat_liable ?? true,
            consignment_warehouse_id: r.consignment_warehouse_id ?? null,
          }));
          // Phase 40: expose ALL supplier company_ids so the catalog fetches
          // products from every supplier this agent is registered with.
          // Before a client is selected the agent should be able to see products
          // from all their suppliers; once a client is selected, setActiveBranch
          // narrows allowedCompanyIds to [client.company_id].
          const allAgentCompanyIds = [
            ...new Set(agentClients.map(r => r.company_id).concat(ag.companyId)),
          ];

          // ── Phase 74: fetch the agent's distinct companies ─────────────
          let availableCompanies: AgentCompanyOption[] = [];
          try {
            const { data, error } = await supabase
              .rpc('get_agent_companies_for_jwt');
            if (!error && Array.isArray(data) && data.length > 0) {
              availableCompanies = (data as { company_id: string; company_name: string | null }[])
                .map(r => ({
                  company_id:   r.company_id,
                  company_name: r.company_name ?? '',
                }));
            } else if (error) {
              console.warn(
                '[Phase 74] get_agent_companies_for_jwt RPC failed:',
                error.message,
                '— falling back to client-derived list.',
              );
            }
          } catch (e) {
            console.warn('[Phase 74] get_agent_companies_for_jwt threw:', e);
          }
          // Fallback: derive the list from agentClients + agentRecord.company_id.
          if (availableCompanies.length === 0 && allAgentCompanyIds.length > 0) {
            const { data: companies } = await supabase
              .from('companies')
              .select('id, legal_name')
              .in('id', allAgentCompanyIds);
            const nameById: Record<string, string> = {};
            for (const c of companies ?? []) {
              nameById[(c as { id: string }).id] =
                (c as { legal_name?: string }).legal_name ?? '';
            }
            availableCompanies = allAgentCompanyIds.map(id => ({
              company_id:   id,
              company_name: nameById[id] ?? '',
            }));
          }

          // Phase 74: pick the initial active company.
          const preferred = availableCompanies.find(c => c.company_id === ag.companyId);
          const activeCompanyId =
            preferred?.company_id
            ?? availableCompanies[0]?.company_id
            ?? ag.companyId
            ?? null;

          // When > 1 company, narrow catalog to active one to prevent leakage.
          const initialAllowed =
            availableCompanies.length > 1 && activeCompanyId
              ? [activeCompanyId]
              : allAgentCompanyIds;

          if (cancelled) return;

          const restored: B2BUserSession = {
            role:               'agent',
            profileId:          ag.agentProfileId ?? ag.userId,
            agentRecord:        ag.agentProfileId ? {
              id:                           ag.agentProfileId,
              company_id:                   ag.companyId,
              name:                         ag.agentName ?? '',
              email:                        ag.agentEmail,
              phone:                        ag.agentPhone,
              sales_commission_rate:        ag.salesCommissionRate,
              collection_commission_rate:   ag.collectionCommissionRate,
              is_master_agent:              !!(activeCompanyId && (ag.masterAgentCompanyIds ?? []).includes(activeCompanyId)),
              default_warehouse_id:         ag.defaultWarehouseId ?? null,
            } : null,
            masterAgentCompanyIds: ag.masterAgentCompanyIds ?? [],
            activeClientId:     null,
            activeClientRecord: null,
            clientRecords:      [],
            agentClientRecords: agentClients,
            allowedCompanyIds:  initialAllowed,
            pricelistByCompany: {},
            availableCompanies,
            activeCompanyId,
          };
          seedB2BUser(restored);
        };

        if (classified.type === 'b2b_client') {
          const b2b = classified.b2bSession!;
          const first = b2b.clientRecords[0];
          if (!first) return;

          const companyIds = [...new Set(b2b.clientRecords.map(r => r.company_id))];
          // Use pre-fetched companies from the SECURITY DEFINER RPC when available,
          // falling back to a direct query (which works when the user has company_members).
          let nameById: Record<string, string> = {};
          if (b2b.companies && b2b.companies.length > 0) {
            for (const c of b2b.companies) {
              nameById[c.id] = c.legal_name ?? '';
            }
          } else {
            const { data: companies } = await supabase
              .from('companies')
              .select('id, legal_name')
              .in('id', companyIds);
            for (const c of companies ?? []) {
              nameById[(c as { id: string }).id] =
                (c as { legal_name?: string }).legal_name ?? '';
            }
          }
          const availableCompanies: AgentCompanyOption[] = companyIds.map(id => ({
            company_id:   id,
            company_name: nameById[id] ?? '',
          }));

          const activeCompanyId = first.company_id;
          const restored: B2BUserSession = {
            role:               'client',
            profileId:          first.id,
            agentRecord:        null,
            activeClientId:     first.id,
            activeClientRecord: {
              id:           first.id,
              company_id:   first.company_id,
              name:         first.name,
              email:        first.email,
              phone:        first.phone,
              pricelist_id: first.pricelist_id,
              business_id:  first.business_id ?? null,
              is_vat_liable: first.is_vat_liable ?? true,
            },
            clientRecords:      b2b.clientRecords,
            agentClientRecords: [],
            allowedCompanyIds:  [activeCompanyId],
            pricelistByCompany: b2b.pricelistByCompany,
            availableCompanies,
            activeCompanyId,
          };
          seedB2BUser(restored);
        } else if (classified.type === 'field_agent') {
          await seedAgent(classified.agentSession!);
        } else if (classified.type === 'dual_role') {
          // Dual-role hard-refresh: use URL path to infer which role to restore.
          // /b2b/agent* → the user chose agent on the Role Selector before refresh.
          // Any other /b2b/* path → restore as client (catalog, dashboard, etc.)
          if (location.pathname.startsWith('/b2b/agent')) {
            await seedAgent(classified.agentSession!);
          } else {
            // Restore as client (same logic as b2b_client case above)
            const b2b   = classified.b2bSession!;
            const first = b2b.clientRecords[0];
            if (first) {
              const companyIds = [...new Set(b2b.clientRecords.map(r => r.company_id))];
              let nameById: Record<string, string> = {};
              if (b2b.companies && b2b.companies.length > 0) {
                for (const c of b2b.companies) {
                  nameById[c.id] = c.legal_name ?? '';
                }
              } else {
                const { data: companies } = await supabase
                  .from('companies')
                  .select('id, legal_name')
                  .in('id', companyIds);
                for (const c of companies ?? []) {
                  nameById[(c as { id: string }).id] =
                    (c as { legal_name?: string }).legal_name ?? '';
                }
              }
              const availableCompanies: AgentCompanyOption[] = companyIds.map(id => ({
                company_id:   id,
                company_name: nameById[id] ?? '',
              }));

              const activeCompanyId = first.company_id;
              const restoredClient: B2BUserSession = {
                role:               'client',
                profileId:          first.id,
                agentRecord:        null,
                activeClientId:     first.id,
                activeClientRecord: {
                  id:           first.id,
                  company_id:   first.company_id,
                  name:         first.name,
                  email:        first.email,
                  phone:        first.phone,
                  pricelist_id: first.pricelist_id,
                  business_id:  first.business_id ?? null,
                },
                clientRecords:      b2b.clientRecords,
                agentClientRecords: [],
                allowedCompanyIds:  [activeCompanyId],
                pricelistByCompany: b2b.pricelistByCompany,
                availableCompanies,
                activeCompanyId,
              };
              seedB2BUser(restoredClient);
            }
          }
        } else {
          // 'unknown' — the user has a valid Supabase token but is not registered
          // as a B2B client or agent (e.g. company_members row exists but
          // inventory_agents row is missing / blocked by RLS after Phase 38.3 gate).
          //
          // Without this branch the page hangs forever: session stays null,
          // the restorer effect doesn't re-run (no deps changed), and every child
          // component that renders `if (!session) return <Loader2 />` spins
          // indefinitely — the "infinite loading" bug reported in Phase 38.4.
          //
          // Fix: sign out immediately and redirect to the login page with a
          // descriptive error message carried via location state.
          if (!cancelled) {
            supabase.auth.signOut().finally(() => {
              navigate('/b2b', {
                replace: true,
                state: {
                  accessError:
                    'הגישה נדחתה — פרטי הסוכן לא נמצאו במערכת. ' +
                    'צור קשר עם מנהל המערכת כדי לוודא שהפרופיל שלך הוגדר כראוי.',
                },
              });
            });
          }
        }
      })
      .catch(err => {
        // Network / unexpected error during restore — don't leave the user hanging.
        console.error('[B2BLayout] session restore error:', err);
        if (!cancelled) {
          supabase.auth.signOut().finally(() => {
            navigate('/b2b', { replace: true });
          });
        }
      })
      .finally(() => { if (!cancelled) setSessionLoading(false); });
    return () => { cancelled = true; };
    // location.pathname is intentionally included so that if a dual-role user
    // navigates between /b2b/agent and /b2b/catalog their restored role matches.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, session, seedB2BUser, location.pathname]);

  // ── Phase 74.1: aggressive RPC-fetch for the agent's companies ────────────
  // The Phase 74 seed (in the restore effect above AND in B2BLoginPage) tries
  // to populate `availableCompanies` via get_agent_companies_for_jwt(), but
  // can silently end up with an empty array when:
  //   • PostgREST hasn't yet exposed the freshly-applied RPC (cache race)
  //   • The fetch errored and the catch block swallowed it
  //   • The agent had only the fallback derivation succeed but with stale
  //     data
  //
  // To make the supplier switcher robust, we run an INDEPENDENT effect here
  // that fires whenever an agent session becomes available. It re-fetches
  // the RPC, falls back to the companies table, and PATCHES the session via
  // setAgentCompanies (which preserves the active client + cart). Verbose
  // [Phase 74.1] console.log statements are intentional so the user / QA can
  // trace why the dropdown is/isn't populated in production.
  const refreshTokenRef = useRef(0);
  const refreshAgentCompanies = useCallback(() => {
    refreshTokenRef.current += 1;
    // eslint-disable-next-line no-console
    console.log('[Phase 74.1] manual refresh requested, token =',
      refreshTokenRef.current);
  }, []);

  const isAgentSession = session?.role === 'agent';
  const sessionAvailableCompanies = session?.availableCompanies ?? null;
  const sessionActiveCompanyId    = session?.activeCompanyId    ?? null;
  const refreshToken              = refreshTokenRef.current;

  useEffect(() => {
    if (!isAgentSession) return;
    let cancelled = false;
    setCompaniesLoading(true);
    // eslint-disable-next-line no-console
    console.log('[Phase 74.1] companies-fetch effect FIRED', {
      isAgent:            isAgentSession,
      activeCompanyId:    sessionActiveCompanyId,
      availableCompanies: sessionAvailableCompanies,
      refreshToken,
    });
    (async () => {
      let companies: AgentCompanyOption[] = [];
      try {
        const { data, error } = await supabase
          .rpc('get_agent_companies_for_jwt');
        if (error) {
          console.warn('[Phase 74.1] RPC get_agent_companies_for_jwt error:',
            error.message, error);
        } else if (Array.isArray(data) && data.length > 0) {
          companies = (data as { company_id: string; company_name: string | null }[])
            .map(r => ({
              company_id:   r.company_id,
              company_name: r.company_name ?? '',
            }));
          // eslint-disable-next-line no-console
          console.log('[Phase 74.1] RPC returned', companies.length,
            'companies:', companies);
        } else {
          // eslint-disable-next-line no-console
          console.log('[Phase 74.1] RPC returned 0 rows — will try fallback');
        }
      } catch (e) {
        console.warn('[Phase 74.1] RPC threw:', e);
      }

      // Fallback: derive from agentClientRecords + agentRecord.company_id.
      if (companies.length === 0) {
        const fallbackIds = [
          ...new Set([
            ...(session?.agentClientRecords ?? []).map(r => r.company_id),
            ...(session?.agentRecord?.company_id ? [session.agentRecord.company_id] : []),
          ]),
        ].filter(Boolean);
        if (fallbackIds.length > 0) {
          const { data, error } = await supabase
            .from('companies')
            .select('id, legal_name')
            .in('id', fallbackIds);
          if (error) {
            console.warn('[Phase 74.1] fallback companies fetch error:',
              error.message);
          } else {
            const nameById: Record<string, string> = {};
            for (const c of data ?? []) {
              nameById[(c as { id: string }).id] =
                (c as { legal_name?: string }).legal_name ?? '';
            }
            companies = fallbackIds.map(id => ({
              company_id:   id,
              company_name: nameById[id] ?? '',
            }));
            // eslint-disable-next-line no-console
            console.log('[Phase 74.1] fallback derived', companies.length,
              'companies:', companies);
          }
        }
      }

      if (cancelled) return;
      // eslint-disable-next-line no-console
      console.log('[Phase 74.1] applying setAgentCompanies with',
        companies.length, 'companies');
      setAgentCompanies(companies);
    })()
      .catch(e => console.warn('[Phase 74.1] effect outer error:', e))
      .finally(() => { if (!cancelled) setCompaniesLoading(false); });

    return () => { cancelled = true; };
    // We intentionally include refreshToken so the manual refresh button
    // re-fires this effect even when no other dep changed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAgentSession, refreshToken, setAgentCompanies]);

  // ── Supplier names (company_id → legal_name) for branch switcher ──────────
  const [supplierNames, setSupplierNames] = useState<Record<string, string>>({}); 

  useEffect(() => {
    // For clients: use allowedCompanyIds.
    // For agents: collect all company_ids from their client records so the
    // BranchSwitcher can display the supplier name under each client entry.
    const baseIds = session?.allowedCompanyIds ?? [];
    const agentIds = (session?.agentClientRecords ?? []).map(r => r.company_id);
    const companyIds = [...new Set([...baseIds, ...agentIds])];
    if (companyIds.length === 0) return;
    supabase
      .from('companies')
      .select('id, legal_name')
      .in('id', companyIds)
      .then(({ data }) => {
        if (!data) return;
        const map: Record<string, string> = {};
        for (const row of data) map[row.id as string] = (row.legal_name as string) ?? '';
        setSupplierNames(map);
      });
  }, [session?.allowedCompanyIds, session?.agentClientRecords]);

  // ── Phase 73: fetch allow_consignment for every client we may order for ──
  // Clients (single profile across multiple branches): all clientRecords.
  // Agents (act on behalf of clients): all agentClientRecords.
  // We cache the boolean per client_id so the cart footer toggle and the
  // confirmation modal interceptor below can decide what to show without
  // hitting the DB on each render.
  useEffect(() => {
    const ids = [
      ...new Set([
        ...(session?.clientRecords      ?? []).map(r => r.id),
        ...(session?.agentClientRecords ?? []).map(r => r.id),
      ]),
    ].filter(Boolean) as string[];
    if (ids.length === 0) {
      setAllowConsignmentByClient({});
      return;
    }
    let cancelled = false;
    supabase
      .from('inventory_clients')
      .select('id, allow_consignment, allow_standard_sales')
      .in('id', ids)
      .then(({ data, error }) => {
        if (cancelled) return;
        if (error) {
          console.warn('[Phase 73] failed to load client flags:', error.message);
          setAllowConsignmentByClient({});
          setAllowStandardByClient({});
          return;
        }
        const conMap: Record<string, boolean> = {};
        const stdMap: Record<string, boolean> = {};
        for (const row of (data ?? []) as { id: string; allow_consignment?: boolean; allow_standard_sales?: boolean }[]) {
          conMap[row.id] = !!row.allow_consignment;
          stdMap[row.id] = row.allow_standard_sales !== false;
        }
        setAllowConsignmentByClient(conMap);
        setAllowStandardByClient(stdMap);
      });
    return () => { cancelled = true; };
  }, [session?.clientRecords, session?.agentClientRecords]);

  // ── Toast helper ──────────────────────────────────────────────────────────
  const showToast = useCallback((kind: ToastKind, message: string) => {
    setToast({ kind, message });
    setTimeout(() => setToast(null), 4500);
  }, []);

  // ── Sign-out ──────────────────────────────────────────────────────────────
  const handleSignOut = async () => {
    await supabase.auth.signOut();
    navigate('/b2b', { replace: true });
  };

  // ── Switch client (agent only) ────────────────────────────────────────────
  const handleSwitchClient = () => {
    clearActiveClient();
    clearCart();
    navigate('/b2b/agent');
  };

  // ── Resolve order context from B2BUserContext ─────────────────────────────
  // Phase 33: For each cart item, use the item's product.company_id to find
  // the matching client record (the branch registered with that supplier).
  // This supports multi-supplier carts correctly.
  const resolveCtx = useCallback((productCompanyId: string) => {
    if (!session) return null;

    if (session.role === 'client') {
      // Find the client record for the specific supplier of this product
      const record =
        session.clientRecords.find(r => r.company_id === productCompanyId) ??
        session.activeClientRecord;
      if (!record) return null;
      return {
        company_id: record.company_id,
        client_id:  record.id,
        agent_id:   null as string | null,
      };
    }

    if (session.role === 'agent') {
      const record = session.activeClientRecord;
      if (!record) return null;
      // Phase 115: When a master agent orders for a client assigned to another
      // agent, default commission credit to the assigned agent.
      const isMaster = session.agentRecord?.is_master_agent ?? false;
      const assignedId = (record as unknown as Record<string, unknown>).assigned_agent_id as string | null;
      const ownId = session.agentRecord?.id ?? null;
      const isOwnClient = !assignedId || assignedId === ownId;
      return {
        company_id:       record.company_id,
        client_id:        record.id,
        agent_id:         user?.id ?? null,
        selling_agent_id: (isMaster && !isOwnClient) ? assignedId : null,
      };
    }

    return null;
  }, [session, user]);

  // Legacy single-context resolver (for cart footer warning display)
  const resolveAnyCtx = useCallback(() => {
    if (!session) return null;
    if (session.role === 'client') {
      const record = session.activeClientRecord;
      if (!record) return null;
      return { company_id: record.company_id, client_id: record.id, agent_id: null as string | null };
    }
    if (session.role === 'agent') {
      const record = session.activeClientRecord;
      if (!record) return null;
      return { company_id: record.company_id, client_id: record.id, agent_id: user?.id ?? null };
    }
    return null;
  }, [session, user]);

  // ── Phase 73: which client_ids does this submission cover? ──────────────
  // We need this to know whether to show the "double-check" modal: if ANY of
  // the client records targeted by the cart is consignment-eligible, the
  // user must make a conscious choice between standard / consignment.
  const collectClientIdsForCart = useCallback((): string[] => {
    if (!session) return [];
    const groups: Record<string, true> = {};
    for (const item of items) groups[item.product.company_id] = true;
    const ids: string[] = [];
    for (const companyId of Object.keys(groups)) {
      if (session.role === 'client') {
        const rec =
          session.clientRecords.find(r => r.company_id === companyId) ??
          session.activeClientRecord;
        if (rec?.id) ids.push(rec.id);
      } else if (session.role === 'agent') {
        const rec = session.activeClientRecord;
        if (rec?.id) ids.push(rec.id);
      }
    }
    return [...new Set(ids)];
  }, [session, items]);

  // True iff at least one targeted client has allow_consignment=true.
  const cartHasConsignmentClient = useCallback((): boolean => {
    const ids = collectClientIdsForCart();
    return ids.some(id => allowConsignmentByClient[id] === true);
  }, [collectClientIdsForCart, allowConsignmentByClient]);

  // True iff at least one targeted client has allow_standard_sales=true.
  const cartHasStandardClient = useCallback((): boolean => {
    const ids = collectClientIdsForCart();
    return ids.some(id => allowStandardByClient[id] !== false);
  }, [collectClientIdsForCart, allowStandardByClient]);

  // Dual mode = both consignment AND standard are allowed for the cart's clients.
  const cartIsDualMode = useCallback((): boolean => {
    return cartHasConsignmentClient() && cartHasStandardClient();
  }, [cartHasConsignmentClient, cartHasStandardClient]);

  // Consignment-only = consignment allowed but NOT standard.
  const cartIsConsignmentOnly = useCallback((): boolean => {
    return cartHasConsignmentClient() && !cartHasStandardClient();
  }, [cartHasConsignmentClient, cartHasStandardClient]);

  // ── Phase 73: actual order submission (called by both the direct path and
  //     the confirmation modal). Accepts the chosen order_type explicitly so
  //     the modal can drive it without racing the React state update.
  const finalizeSubmitOrder = useCallback(async (chosenType: B2BOrderType) => {
    if (items.length === 0 || submitting) return;

    // Phase 109: block ordering for view_only clients
    if (session?.role === 'client' && session.activeClientRecord?.client_access === 'view_only') {
      showToast('error', 'חשבונך מוגדר לצפייה בלבד — פנה לסוכן שלך להגשת הזמנות');
      return;
    }

    // Group cart items by company_id so each supplier gets its own order
    const groups: Record<string, typeof items> = {};
    for (const item of items) {
      const cid = item.product.company_id;
      if (!groups[cid]) groups[cid] = [];
      groups[cid].push(item);
    }

    // Check all groups can be resolved before starting any DB write
    for (const companyId of Object.keys(groups)) {
      if (!resolveCtx(companyId)) {
        showToast('error', 'לא ניתן לזהות את הלקוח לאחד הספקים — נסה להתנתק ולהתחבר מחדש');
        return;
      }
    }

    // Phase 73 defensive guard: never let a 'consignment' order through for
    // a client that isn't eligible — even if the UI somehow sent it. The DB
    // guard would reject this anyway, but failing fast here gives a nicer UX.
    if (chosenType === 'consignment') {
      const ids = collectClientIdsForCart();
      const allEligible = ids.length > 0
        && ids.every(id => allowConsignmentByClient[id] === true);
      if (!allEligible) {
        showToast('error', 'לא ניתן לבצע הזמנת קונסגנציה — הלקוח אינו מאושר לקונסגנציה.');
        return;
      }
    }

    setSubmitting(true);
    try {
      const results = await Promise.all(
        Object.entries(groups).map(([companyId, groupItems]) => {
          const ctx = resolveCtx(companyId)!;
          return submitOrder(
            ctx,
            groupItems.map(i => ({
              product_id:         i.product.id,
              requested_quantity: i.quantity,
              unit_price:         i.product.price,
            })),
            instantDelivery,
            chosenType,
          );
        }),
      );

      const totalLineCount    = results.reduce((s, r) => s + r.line_count, 0);
      const deliveryNoteCount = results.filter(r => r.delivery_note_id).length;

      clearCart();
      setCartOpen(false);
      setInstantDelivery(false);   // reset toggle after submission
      setOrderType('standard');     // Phase 73: reset for next order

      const consignmentLabel = chosenType === 'consignment' ? ' (קונסגנציה)' : '';

      // Phase 46: differentiate toast message for Van Sale orders
      if (instantDelivery && deliveryNoteCount > 0) {
        showToast(
          'success',
          results.length === 1
            ? `הזמנה${consignmentLabel} + תעודת משלוח הופקו בהצלחה! (${totalLineCount} פריטים)`
            : `${results.length} הזמנות${consignmentLabel} + תעודות משלוח הופקו בהצלחה! (${totalLineCount} פריטים)`,
        );
      } else if (instantDelivery && deliveryNoteCount === 0) {
        // Order was locked but doc type is missing in ERP
        showToast(
          'success',
          `ההזמנה${consignmentLabel} נשלחה ונעולה (${totalLineCount} פריטים). תעודת משלוח לא הופקה — הגדר סוג מסמך בהגדרות ERP.`,
        );
      } else {
        showToast(
          'success',
          results.length === 1
            ? `ההזמנה${consignmentLabel} נשלחה בהצלחה! (${totalLineCount} פריטים)`
            : `${results.length} הזמנות${consignmentLabel} נשלחו בהצלחה! (${totalLineCount} פריטים)`,
        );
      }
      navigate('/b2b/orders');
    } catch (err) {
      showToast('error', err instanceof Error ? err.message : 'שגיאה בלתי צפויה — נסה שנית');
    } finally {
      setSubmitting(false);
    }
  }, [
    items, submitting, instantDelivery, resolveCtx, clearCart, navigate,
    showToast, collectClientIdsForCart, allowConsignmentByClient,
  ]);

  // ── Submit order — entry point from the cart footer button ──────────────
  // Phase 73 "double check" interceptor: when at least one client targeted by
  // the cart is consignment-eligible, ALWAYS open the confirmation modal so
  // the user picks standard vs consignment explicitly — even if they already
  // toggled the footer chooser. This prevents accidental misclassification.
  const handleSubmitOrder = useCallback(async () => {
    if (items.length === 0 || submitting) return;
    await remindTermsIfNeeded();

    if (cartIsConsignmentOnly()) {
      // Consignment-only client → submit as consignment immediately, no toggle.
      await finalizeSubmitOrder('consignment');
      return;
    }
    if (cartIsDualMode()) {
      // Dual mode → user must pick explicitly.
      setConfirmModalOpen(true);
      return;
    }
    // Standard-only client → submit as standard immediately.
    await finalizeSubmitOrder('standard');
  }, [items.length, submitting, cartIsConsignmentOnly, cartIsDualMode, finalizeSubmitOrder, remindTermsIfNeeded]);

  // Phase 73 — confirmation modal callbacks. Each closes the modal, sets the
  // footer toggle to reflect the user's choice (so the UI stays consistent
  // across multi-step flows) and forwards the chosen type to the actual
  // submission pipeline.
  const handleConfirmConsignment = useCallback(async () => {
    setConfirmModalOpen(false);
    setOrderType('consignment');
    await finalizeSubmitOrder('consignment');
  }, [finalizeSubmitOrder]);

  const handleConfirmStandard = useCallback(async () => {
    setConfirmModalOpen(false);
    setOrderType('standard');
    await finalizeSubmitOrder('standard');
  }, [finalizeSubmitOrder]);

  // ── Nav helpers ───────────────────────────────────────────────────────────
  //
  // navCls       — mobile bottom-tab pills (icon stacked above label).
  // sidebarCls   — md+ vertical rail items (icon stacked above label, square
  //                tile with active accent). Wider hit-area + tooltip-friendly
  //                title attr is set on the NavLink itself below.
  const navCls = ({ isActive }: { isActive: boolean }) =>
    [
      'flex flex-col items-center gap-0.5 px-3 py-1.5 rounded-xl transition-colors text-xs font-medium',
      isActive ? 'text-primary-600 bg-primary-50' : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100',
    ].join(' ');

  const sidebarCls = ({ isActive }: { isActive: boolean }) =>
    [
      'group relative flex flex-col items-center justify-center gap-1 w-14 h-14 mx-auto',
      'rounded-2xl transition-colors text-[10px] font-semibold leading-none',
      isActive
        ? 'text-primary-600 bg-primary-50 ring-1 ring-primary-100'
        : 'text-gray-500 hover:text-gray-800 hover:bg-gray-100',
    ].join(' ');

  const isAgent     = session?.role === 'agent';
  const isClient    = session?.role === 'client';
  const hasClient   = !!session?.activeClientRecord;
  const isClientViewOnly = isClient && session?.activeClientRecord?.client_access === 'view_only';
  const orderCtx    = resolveAnyCtx();

  // True iff the currently-active client has consignment enabled
  const activeClientIsConsignment = !!(
    session?.activeClientId &&
    allowConsignmentByClient[session.activeClientId] === true
  );

  // Branch switcher records:
  //   - For 'client': clientRecords (branches of the same business user)
  //   - For 'agent' : agentClientRecords (all clients assigned to this agent)
  //                   — Phase 74: when the agent has > 1 supplier company,
  //                   the list is cascade-filtered to clients of the
  //                   currently active company (activeCompanyId) so cross-
  //                   supplier clients never leak into the picker.
  const clientRecords = session?.clientRecords ?? [];

  // Phase 74: full agent-client list (unfiltered) and cascading filter.
  const allAgentClientRecords: AgentClientRecord[] =
    session?.agentClientRecords ?? [];

  // Cast clientRecords to AgentClientRecord shape (compatible subset)
  const branchRecords: AgentClientRecord[] = isAgent
    ? (
        // Phase 74 cascading filter: when the agent has more than one
        // company, scope the picker to clients of the active company.
        // When they have exactly one company we show the full list (no
        // cascade needed).
        (session?.availableCompanies?.length ?? 0) > 1 && session?.activeCompanyId
          ? allAgentClientRecords.filter(
              r => r.company_id === session.activeCompanyId,
            )
          : allAgentClientRecords
      )
    : clientRecords
        // Scope to the active supplier only — no cross-supplier leakage.
        .filter(r => r.company_id === session?.activeCompanyId)
        .map(r => ({
          id:           r.id,
          company_id:   r.company_id,
          name:         r.name,
          email:        r.email ?? null,
          phone:        r.phone ?? null,
          pricelist_id: r.pricelist_id ?? null,
          business_id:  (r as any).business_id ?? null,
        }));

  // Show branch switcher for clients always, for agents when they have clients
  const showBranchSwitcher = isClient || (isAgent && branchRecords.length > 0);

  // Phase 74.1: company switcher visibility — RELAXED.
  // Always render for agents so the user can visually confirm the component
  // is mounted and the RPC fetch is taking place. The CompanySwitcher
  // component itself handles the loading / 0-companies / 1-company states
  // by rendering a disabled label and a manual refresh button. This
  // eliminates the silent "missing dropdown" failure mode where a strict
  // `availableCompanies.length > 1` gate hid the UI before the RPC data
  // had reached React state (cache races, RLS hiccups, etc.).
  const availableCompanies = session?.availableCompanies ?? [];
  const showCompanySwitcher =
    (isAgent && (session?.availableCompanies?.length ?? 0) > 1)
    || (isClient && (session?.availableCompanies?.length ?? 0) > 1);

  // Phase 74.1: debug print so we can trace WHY the dropdown might be empty
  // in production.  These run on every render of the Shell; they're cheap
  // and intentional — see the aggressive RPC-fetch effect above for the
  // detailed [Phase 74.1] traces.
  // eslint-disable-next-line no-console
  console.log('[Phase 74.1] render', {
    isAgent,
    activeCompanyId:    session?.activeCompanyId ?? null,
    availableCompanies,
    companiesLoading,
    showCompanySwitcher,
  });

  // Phase 74: handle company switching — atomically clears the active
  // client + cart and rescopes catalog/orders/returns to the new supplier.
  // Sends the agent back to the client picker so they can choose a client
  // belonging to the newly selected company before continuing.
  const handleSwitchCompany = useCallback((companyId: string) => {
    if (!session) return;
    if (session.activeCompanyId === companyId) return;
    setActiveCompany(companyId);
    clearCart();
    if (session.role === 'agent') {
      navigate('/b2b/agent');
    } else {
      navigate('/b2b/catalog');
    }
  }, [session, setActiveCompany, clearCart, navigate]);

  return (
    <div dir="rtl" className="min-h-screen bg-gray-50 flex flex-col">

      {/* ── Toast ───────────────────────────────────────────────────────── */}
      {toast && (
        <div className={[
          'fixed top-4 right-4 left-4 md:left-auto md:w-80 z-[60]',
          'flex items-start gap-3 px-4 py-3 rounded-2xl shadow-xl text-sm font-medium',
          toast.kind === 'success' ? 'bg-green-600 text-white' : 'bg-red-600 text-white',
        ].join(' ')}>
          {toast.kind === 'success'
            ? <CheckCircle2 className="w-4 h-4 shrink-0 mt-0.5" />
            : <AlertCircle  className="w-4 h-4 shrink-0 mt-0.5" />
          }
          <span className="flex-1">{toast.message}</span>
          <button onClick={() => setToast(null)} className="p-0.5 opacity-70 hover:opacity-100">
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      )}

      {/* ── Top header ──────────────────────────────────────────────────── */}
      <header className="sticky top-0 z-30 bg-white border-b border-gray-100 shadow-sm">
        <div className="max-w-5xl mx-auto px-2 sm:px-4 min-h-[3.5rem] py-1.5 flex items-center justify-between gap-1.5 sm:gap-2">

          {/* Brand + role badge */}
          <div className="flex items-center gap-1.5 sm:gap-2 shrink-0">
            <ShoppingBag className="w-5 h-5 text-primary-600" />
            <div className="hidden sm:block">
              <span className="font-bold text-gray-900 text-sm leading-none">
                הזמנות B2B
              </span>
              {/* Agent identity — shown in header when role is agent */}
              {isAgent && session?.agentRecord && (
                <p className="text-[10px] text-orange-600 font-medium leading-none mt-0.5 truncate max-w-[160px]">
                  חשבון סוכן: {session.agentRecord.name}
                  {session.agentRecord.email && (
                    <span className="text-gray-400 font-normal"> · {session.agentRecord.email}</span>
                  )}
                </p>
              )}
            </div>
            {isAgent && (
              <span className="hidden sm:inline-flex items-center gap-1 px-2 py-0.5 rounded-full
                               bg-orange-100 text-orange-700 text-[10px] font-semibold">
                <Briefcase className="w-2.5 h-2.5" />
                סוכן
              </span>
            )}
          </div>

          {/* Phase 68 — desktop horizontal nav was removed in favour of the
              right-side vertical Sidebar Rail rendered below the header.
              The header now keeps only the brand, the client/branch switcher,
              the cart, and the sign-out button — keeping it visually clean
              on tablets and desktops. The mobile bottom tab bar is unchanged. */}
          <div className="flex-1" />

          {/* Right side: company switcher + branch switcher + user email + cart + sign-out */}
          <div className="flex items-center gap-1 sm:gap-2 min-w-0">

            {/* ── Branch / Business Switcher (Phase 33.1 – always rendered) ── */}
            {sessionLoading && (
              <div className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-xl border
                              border-gray-200 text-xs text-gray-400">
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
                <span className="hidden sm:block">טוען…</span>
              </div>
            )}

            {/* ── Phase 74: Multi-Company Supplier Switcher (agents only) ──
                Rendered ONLY when the agent represents more than one company.
                Switching the company atomically clears the active client +
                cart and rescopes catalog / orders / returns to the new
                supplier — see handleSwitchCompany above. */}
            {!sessionLoading && showCompanySwitcher && (
              <CompanySwitcher
                companies={availableCompanies}
                activeCompanyId={session?.activeCompanyId ?? null}
                onSelect={handleSwitchCompany}
                loading={companiesLoading}
                disabled={availableCompanies.length <= 1}
                onRefresh={refreshAgentCompanies}
              />
            )}

            {!sessionLoading && showBranchSwitcher && (
              <BranchSwitcher
                clientRecords={branchRecords}
                activeBranchId={session?.activeClientId ?? null}
                onSelect={record => {
                  setActiveBranch(record);
                  // For agents: selecting a client from the header switcher
                  // should navigate to the catalog (same as B2BClientSelection).
                  if (isAgent) navigate('/b2b/catalog');
                }}
                supplierNames={supplierNames}
                dropdownLabel={isAgent ? 'בחירת לקוח' : 'החלפת סניף / עסק'}
                placeholder={isAgent ? 'בחר לקוח' : 'בחר סניף'}
              />
            )}

            {/* User name (hidden on small screens) */}
            <span className="hidden lg:block text-xs text-gray-400 max-w-[120px] truncate">
              {session?.activeClientRecord?.name
                ?? session?.clientRecords[0]?.name
                ?? user?.email
                ?? user?.phone
                ?? ''}
            </span>

            {/* Cart FAB — only when an order context is available; hidden for view-only clients */}
            {(!isAgent || hasClient) && !isClientViewOnly && (
              <button
                onClick={() => setCartOpen(true)}
                className="relative flex items-center justify-center w-8 h-8 sm:w-10 sm:h-10 rounded-xl
                           bg-primary-600 text-white shadow hover:bg-primary-700 transition-colors"
                aria-label="פתח עגלה"
              >
                <ShoppingCart className="w-5 h-5" />
                {uniqueItems > 0 && (
                  <span className="absolute -top-1.5 -left-1.5 min-w-[18px] h-[18px] px-1
                                   bg-red-500 text-white text-[10px] font-bold rounded-full
                                   flex items-center justify-center leading-none">
                    {uniqueItems > 99 ? '99+' : uniqueItems}
                  </span>
                )}
              </button>
            )}

            {/* Sign out */}
            <button
              onClick={handleSignOut}
              className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-xl transition-colors"
              aria-label="התנתק"
              title="התנתק"
            >
              <LogOut className="w-4 h-4" />
            </button>
          </div>
        </div>
      </header>

      {/* ── Agent "Acting as" banner ─────────────────────────────────────── */}
      {isAgent && hasClient && session.activeClientRecord && (
        <div className="sticky top-14 z-20 bg-orange-50 border-b border-orange-200">
          <div className="max-w-5xl mx-auto px-4 py-2 flex items-center justify-between gap-3">
            <div className="flex items-center gap-2 min-w-0">
              <Briefcase className="w-4 h-4 text-orange-500 shrink-0" />
              <div className="min-w-0">
                <p className="text-sm text-orange-800 font-medium leading-tight">
                  מגיש הזמנה עבור:{' '}
                  <strong className="font-semibold">{session.activeClientRecord.name}</strong>
                </p>
                {session.agentRecord && (
                  <p className="text-[10px] text-orange-500 leading-tight truncate">
                    סוכן: {session.agentRecord.name}
                    {session.agentRecord.email && ` · ${session.agentRecord.email}`}
                  </p>
                )}
              </div>
            </div>
            <button
              onClick={handleSwitchClient}
              className="flex items-center gap-1.5 text-xs font-semibold text-orange-700
                         hover:text-orange-900 bg-white border border-orange-200 hover:bg-orange-50
                         px-3 py-1.5 rounded-lg transition-colors shrink-0"
            >
              <ArrowLeftRight className="w-3 h-3" />
              החלף לקוח
            </button>
          </div>
        </div>
      )}

      {/* ── Sidebar Rail (tablet & desktop, RTL → fixed to the RIGHT) ─────
          Phase 68: vertical navigation rail replaces the horizontal top nav
          on md+. Rendered as <aside> with role="navigation" so screen readers
          announce it as a landmark. Width is fixed at w-20 (80px), matching
          the md:pr-20 padding applied to <main> below so content never sits
          underneath the rail. Hidden entirely on mobile — bottom tab bar
          handles navigation there. */}
      <aside
        role="navigation"
        aria-label="ניווט ראשי"
        className="hidden md:flex flex-col items-stretch
                   fixed top-14 right-0 bottom-0 z-20
                   w-20 bg-white border-l border-gray-100
                   shadow-[-1px_0_8px_rgba(0,0,0,0.04)]
                   py-3 gap-1 overflow-y-auto"
      >
        {isAgent && !hasClient ? (
          <>
            <NavLink to="/b2b/agent" className={sidebarCls} title="בחירת לקוח">
              <Users className="w-5 h-5" />
              <span>בחירה</span>
            </NavLink>
            {/* Phase 52: Customer management — agents only */}
            <NavLink to="/b2b/customers" className={sidebarCls} title="לקוחות">
              <Users className="w-5 h-5" />
              <span>לקוחות</span>
            </NavLink>
            {/* Phase 51: Price List management — agents only */}
            <NavLink to="/b2b/pricelists" className={sidebarCls} title="מחירונים">
              <Tags className="w-5 h-5" />
              <span>מחירונים</span>
            </NavLink>
          </>
        ) : (
          <>
            <NavLink to="/b2b/dashboard" className={sidebarCls} title="דשבורד">
              <LayoutDashboard className="w-5 h-5" />
              <span>דשבורד</span>
            </NavLink>
            <NavLink to="/b2b/catalog" className={sidebarCls} title="קטלוג">
              <BookOpen className="w-5 h-5" />
              <span>קטלוג</span>
            </NavLink>
            <NavLink to="/b2b/orders" className={sidebarCls} title="הזמנות">
              <ClipboardList className="w-5 h-5" />
              <span>הזמנות</span>
            </NavLink>
            {/* Phase 67: consolidated LIFO returns — single rail item for all
                purchased products. Per-order RMA is also reachable from a
                "בקש החזרה" button on each delivered order card.
                Phase 112: hidden when b2b_returns_enabled=false */}
            {returnsEnabled && (
              <NavLink to="/b2b/returns" className={sidebarCls} title="החזרות">
                <RotateCcw className="w-5 h-5" />
                <span>החזרות</span>
              </NavLink>
            )}
            {showTerms && (
              <NavLink to="/b2b/terms" className={sidebarCls} title="תנאים">
                <FileText className="w-5 h-5" />
                <span>תנאים</span>
              </NavLink>
            )}
            {/* Phase 90: Consignment sale report — visible only when active client has consignment */}
            {activeClientIsConsignment && (
              <NavLink to="/b2b/consignment-report" className={sidebarCls} title="דיווח קונסגנציה">
                <ClipboardCheck className="w-5 h-5" />
                <span>דיווח מכירות</span>
              </NavLink>
            )}
            {isClient && activeClientIsConsignment && (
              <NavLink to="/b2b/my-stock" className={sidebarCls} title="יתרות המלאי שלי">
                <Package className="w-5 h-5" />
                <span>המלאי שלי</span>
              </NavLink>
            )}

            {/* Agent-only rail items, even when a client is selected */}
            {isAgent && (
              <>
                {/* Subtle divider so agent tools are visually grouped */}
                <div className="mx-3 my-1 border-t border-gray-100" />
                <NavLink to="/b2b/customers" className={sidebarCls} title="לקוחות">
                  <Users className="w-5 h-5" />
                  <span>לקוחות</span>
                </NavLink>
                <NavLink to="/b2b/pricelists" className={sidebarCls} title="מחירונים">
                  <Tags className="w-5 h-5" />
                  <span>מחירונים</span>
                </NavLink>
                <NavLink to="/b2b/inventory" className={sidebarCls} title="מלאי">
                  <Package className="w-5 h-5" />
                  <span>מלאי</span>
                </NavLink>
                <NavLink to="/b2b/movements" className={sidebarCls} title="תנועות">
                  <ArrowLeftRight className="w-5 h-5" />
                  <span>תנועות</span>
                </NavLink>
              </>
            )}
          </>
        )}
      </aside>

      {/* ── Page content ──────────────────────────────────────────────────
          md:pr-20 reserves horizontal space for the right-side sidebar rail
          (RTL: rail is on the right → padding-right). pb-20 keeps content
          above the mobile bottom-tab bar; md:pb-0 removes that on tablets+. */}
      <main className="flex-1 pb-20 md:pb-0 md:pr-20">
        <Outlet />
      </main>

      {/* ── Bottom tab bar (mobile only) ────────────────────────────────── */}
      <nav className="md:hidden fixed bottom-0 inset-x-0 z-30 bg-white border-t border-gray-100
                      safe-area-pb shadow-[0_-1px_8px_rgba(0,0,0,0.06)]">
        <div className="flex justify-around items-stretch h-16 px-2">
          {isAgent && !hasClient ? (
            <>
              <NavLink to="/b2b/agent" className={navCls}>
                <Users className="w-5 h-5" />
                <span>בחירה</span>
              </NavLink>
              {/* Phase 52: Customers — agent mobile tab */}
              <NavLink to="/b2b/customers" className={navCls}>
                <Users className="w-5 h-5" />
                <span>לקוחות</span>
              </NavLink>
              {/* Phase 51: Price Lists — agent mobile tab */}
              <NavLink to="/b2b/pricelists" className={navCls}>
                <Tags className="w-5 h-5" />
                <span>מחירונים</span>
              </NavLink>
              <NavLink to="/b2b/inventory" className={navCls}>
                <Package className="w-5 h-5" />
                <span>מלאי</span>
              </NavLink>
            </>
          ) : (
            <>
              <NavLink to="/b2b/dashboard" className={navCls}>
                <LayoutDashboard className="w-5 h-5" />
                <span>דשבורד</span>
              </NavLink>
              <NavLink to="/b2b/catalog" className={navCls}>
                <BookOpen className="w-5 h-5" />
                <span>קטלוג</span>
              </NavLink>
              <NavLink to="/b2b/orders" className={navCls}>
                <ClipboardList className="w-5 h-5" />
                <span>הזמנות</span>
              </NavLink>
              {/* Phase 67: consolidated LIFO returns — single tab for all
                  purchased products. Per-order RMA is also reachable from
                  a "בקש החזרה" button on each delivered order card.
                  Phase 112: hidden when b2b_returns_enabled=false */}
              {returnsEnabled && (
                <NavLink to="/b2b/returns" className={navCls}>
                  <RotateCcw className="w-5 h-5" />
                  <span>החזרות</span>
                </NavLink>
              )}
              {showTerms && (
                <NavLink to="/b2b/terms" className={navCls}>
                  <FileText className="w-5 h-5" />
                  <span>תנאים</span>
                </NavLink>
              )}
              {/* Phase 90: Consignment sale report — mobile tab */}
              {activeClientIsConsignment && (
                <NavLink to="/b2b/consignment-report" className={navCls}>
                  <ClipboardCheck className="w-5 h-5" />
                  <span>קונסגנציה</span>
                </NavLink>
              )}
              {isClient && activeClientIsConsignment && (
                <NavLink to="/b2b/my-stock" className={navCls}>
                  <Package className="w-5 h-5" />
                  <span>המלאי שלי</span>
                </NavLink>
              )}
              {/* Phase 51/52: Price Lists + Customers — agent mobile tabs (when client selected) */}
              {isAgent && (
                <>
                  <NavLink to="/b2b/inventory" className={navCls}>
                    <Package className="w-5 h-5" />
                    <span>מלאי</span>
                  </NavLink>
                  <NavLink to="/b2b/customers" className={navCls}>
                    <Users className="w-5 h-5" />
                    <span>לקוחות</span>
                  </NavLink>
                </>
              )}
            </>
          )}
        </div>
      </nav>

      {/* ── Cart drawer ─────────────────────────────────────────────────── */}
      {cartOpen && (
        <>
          <div
            className="fixed inset-0 z-40 bg-black/40 backdrop-blur-sm"
            onClick={() => !submitting && setCartOpen(false)}
          />
          <div className="fixed z-50
                          bottom-0 inset-x-0 rounded-t-3xl
                          md:top-0 md:bottom-0 md:left-0 md:right-auto md:w-96 md:rounded-none md:rounded-r-3xl
                          bg-white shadow-2xl flex flex-col max-h-[90dvh] md:max-h-screen">

            {/* Header */}
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
              <div className="flex items-center gap-2">
                <ShoppingCart className="w-5 h-5 text-primary-600" />
                <h2 className="font-bold text-gray-900">
                  עגלת קניות
                  {totalItems > 0 && (
                    <span className="mr-2 text-sm font-normal text-gray-500">
                      ({totalItems} פריטים)
                    </span>
                  )}
                </h2>
              </div>
              <div className="flex items-center gap-1">
                {/* Expand to full-screen overlay */}
                {items.length > 0 && (
                  <button
                    onClick={() => { setCartExpanded(true); }}
                    disabled={submitting}
                    className="p-1.5 text-gray-400 hover:text-gray-700 hover:bg-gray-100
                               rounded-lg transition-colors disabled:opacity-40"
                    title="הרחב"
                  >
                    <Maximize2 className="w-4.5 h-4.5" />
                  </button>
                )}
                <button
                  onClick={() => !submitting && setCartOpen(false)}
                  disabled={submitting}
                  className="p-1.5 text-gray-400 hover:text-gray-700 hover:bg-gray-100
                             rounded-lg transition-colors disabled:opacity-40"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
            <div className="md:hidden absolute top-2 left-1/2 -translate-x-1/2
                            w-10 h-1 bg-gray-200 rounded-full" />

            {/* Agent "acting as" reminder inside cart */}
            {isAgent && session.activeClientRecord && (
              <div className="mx-4 mt-3 px-3 py-2 bg-orange-50 border border-orange-200
                              rounded-xl text-xs text-orange-700 flex items-center gap-2">
                <Briefcase className="w-3.5 h-3.5 shrink-0" />
                <span>הזמנה עבור: <strong>{session.activeClientRecord.name}</strong></span>
              </div>
            )}

            {/* Active branch reminder inside cart */}
            {isClient && session.activeClientRecord && (
              <div className="mx-4 mt-3 px-3 py-2 bg-primary-50 border border-primary-200
                              rounded-xl text-xs text-primary-700 flex items-center gap-2">
                <GitBranch className="w-3.5 h-3.5 shrink-0" />
                <span>
                  הזמנה עבור:{' '}
                  <strong>{session.activeClientRecord.name}</strong>
                  {supplierNames[session.activeClientRecord.company_id] && (
                    <span className="font-normal text-primary-500">
                      {' '}· {supplierNames[session.activeClientRecord.company_id]}
                    </span>
                  )}
                </span>
              </div>
            )}

            {/* Items */}
            <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3">
              {items.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-16 gap-3 text-gray-400">
                  <ShoppingCart className="w-12 h-12 text-gray-200" />
                  <p className="text-sm font-medium">העגלה ריקה</p>
                  <p className="text-xs text-gray-400">הוסף מוצרים מהקטלוג</p>
                </div>
              ) : (
                items.map(({ product, quantity }) => (
                  <div key={product.id}
                       className="flex items-center gap-3 p-3 bg-gray-50 rounded-2xl">
                    <div className="w-14 h-14 shrink-0 rounded-xl overflow-hidden bg-gray-100">
                      {product.image_url
                        ? <img src={product.image_url} alt={product.name} className="w-full h-full object-cover" />
                        : <div className="w-full h-full flex items-center justify-center">
                            <ShoppingBag className="w-6 h-6 text-gray-300" />
                          </div>
                      }
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-gray-900 truncate">{product.name}</p>
                      <p className="text-xs text-gray-500" dir="ltr">{product.sku}</p>
                      {/* Supplier name in cart row */}
                      {product.supplier_name && (
                        <p className="text-[10px] text-gray-400">{product.supplier_name}</p>
                      )}
                      <p className="text-sm font-bold text-primary-600 mt-0.5">
                        ₪{(product.price * quantity).toFixed(2)}
                      </p>
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      <button onClick={() => setQuantity(product.id, quantity - 1)} disabled={submitting}
                              className="w-7 h-7 flex items-center justify-center rounded-lg bg-white border border-gray-200 text-gray-600 hover:bg-gray-100 transition-colors disabled:opacity-40">
                        <Minus className="w-3 h-3" />
                      </button>
                      <span className="w-7 text-center text-sm font-bold text-gray-800">{quantity}</span>
                      <button onClick={() => setQuantity(product.id, quantity + 1)} disabled={submitting}
                              className="w-7 h-7 flex items-center justify-center rounded-lg bg-white border border-gray-200 text-gray-600 hover:bg-gray-100 transition-colors disabled:opacity-40">
                        <Plus className="w-3 h-3" />
                      </button>
                      <button onClick={() => removeItem(product.id)} disabled={submitting}
                              className="w-7 h-7 flex items-center justify-center rounded-lg text-gray-300 hover:text-red-500 hover:bg-red-50 transition-colors mr-1 disabled:opacity-40">
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>

            {/* Footer */}
            {items.length > 0 && (
              <div className="px-5 py-4 border-t border-gray-100 space-y-3 bg-white rounded-b-3xl md:rounded-none">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-500">{cartIsVatLiable ? 'סה״כ לפני מע״מ' : 'סה״כ'}</span>
                  <span className="font-bold text-gray-900 text-base">₪{totalPrice.toFixed(2)}</span>
                </div>
                {!cartIsVatLiable && (
                  <div className="flex items-center justify-between text-xs text-gray-400">
                    <span>פטור ממע״מ</span>
                    <span>—</span>
                  </div>
                )}
                {cartIsVatLiable && vatRate != null && vatRate > 0 && (
                  <>
                    <div className="flex items-center justify-between text-xs text-gray-400">
                      <span>מע״מ ({vatRate}%)</span>
                      <span>₪{(totalPrice * vatRate / 100).toFixed(2)}</span>
                    </div>
                    <div className="flex items-center justify-between text-sm font-bold text-primary-700 border-t border-gray-100 pt-2">
                      <span>סה״כ כולל מע״מ</span>
                      <span>₪{(totalPrice * (1 + vatRate / 100)).toFixed(2)}</span>
                    </div>
                  </>
                )}
                {cartIsVatLiable && (vatRate == null || vatRate === 0) && (
                  <div className="flex items-center justify-between text-xs text-gray-400">
                    <span>מע״מ</span>
                    <span>ייווסף בעת הפקת המסמך</span>
                  </div>
                )}

                {/* Warning when order context can't be resolved */}
                {!orderCtx && (
                  <div className="flex items-start gap-2 text-xs text-amber-700
                                  bg-amber-50 border border-amber-200 rounded-xl px-3 py-2">
                    <AlertCircle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                    <span>
                      פרטי הלקוח לא זוהו.{' '}
                      <button onClick={handleSignOut} className="underline hover:no-underline">
                        התנתק והתחבר מחדש
                      </button>
                      {' '}כדי לשלוח הזמנה.
                    </span>
                  </div>
                )}

                {/* ── Phase 73: Order Type chooser ──────────────────────────
                    Only visible when at least one client targeted by the cart
                    is consignment-eligible (allow_consignment=true). Provides
                    quick visual feedback BEFORE the user clicks "Place Order".
                    Even when the user uses this toggle, the confirmation
                    modal below still appears at submit time so the choice is
                    confirmed deliberately. */}
                {/* Consignment-only: show a static label, no toggle */}
                {cartIsConsignmentOnly() && (
                  <div className="rounded-2xl border-2 border-amber-200 bg-amber-50/40 p-3">
                    <div className="flex items-center gap-1.5 text-xs font-semibold text-amber-900">
                      <Warehouse className="w-3.5 h-3.5" />
                      הזמנה זו תירשם כקונסגנציה (לקוח קונסגנציה בלבד)
                    </div>
                  </div>
                )}

                {/* Dual mode: show the standard/consignment toggle */}
                {cartIsDualMode() && (
                  <div className="rounded-2xl border-2 border-amber-200 bg-amber-50/40 p-3 space-y-2">
                    <div className="flex items-center gap-1.5 text-xs font-semibold text-amber-900">
                      <Warehouse className="w-3.5 h-3.5" />
                      בחירת סוג הזמנה
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <button
                        type="button"
                        onClick={() => setOrderType('standard')}
                        disabled={submitting}
                        aria-pressed={orderType === 'standard'}
                        className={[
                          'flex flex-col items-center justify-center gap-1 px-2 py-2.5 rounded-xl',
                          'text-xs font-semibold transition-all border-2 disabled:opacity-50',
                          orderType === 'standard'
                            ? 'bg-primary-600 border-primary-600 text-white shadow-sm'
                            : 'bg-white border-gray-200 text-gray-600 hover:border-primary-300',
                        ].join(' ')}
                      >
                        <Package className="w-4 h-4" />
                        רכישה רגילה
                      </button>
                      <button
                        type="button"
                        onClick={() => setOrderType('consignment')}
                        disabled={submitting}
                        aria-pressed={orderType === 'consignment'}
                        className={[
                          'flex flex-col items-center justify-center gap-1 px-2 py-2.5 rounded-xl',
                          'text-xs font-semibold transition-all border-2 disabled:opacity-50',
                          orderType === 'consignment'
                            ? 'bg-amber-600 border-amber-600 text-white shadow-sm'
                            : 'bg-white border-gray-200 text-gray-600 hover:border-amber-300',
                        ].join(' ')}
                      >
                        <Warehouse className="w-4 h-4" />
                        הזמנת קונסגנציה
                      </button>
                    </div>
                  </div>
                )}

                {/* ── Phase 46: Van Sale toggle (agents only) ─────────────── */}
                {isAgent && session.agentRecord && (
                  <button
                    type="button"
                    onClick={() => setInstantDelivery(v => !v)}
                    disabled={submitting}
                    className={[
                      'w-full flex items-center gap-3 px-4 py-3 rounded-2xl border-2',
                      'text-right transition-all duration-200 disabled:opacity-50',
                      instantDelivery
                        ? 'border-orange-400 bg-orange-50 text-orange-800'
                        : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300 hover:bg-gray-50',
                    ].join(' ')}
                    aria-pressed={instantDelivery}
                  >
                    {/* Custom toggle pill */}
                    <span
                      className={[
                        'relative inline-flex h-6 w-11 shrink-0 rounded-full border-2 transition-colors duration-200',
                        instantDelivery ? 'bg-orange-500 border-orange-500' : 'bg-gray-200 border-gray-200',
                      ].join(' ')}
                    >
                      <span
                        className={[
                          'inline-block h-5 w-5 rounded-full bg-white shadow transform transition-transform duration-200',
                          instantDelivery ? 'translate-x-5' : 'translate-x-0',
                        ].join(' ')}
                      />
                    </span>

                    <span className="flex-1 min-w-0">
                      <span className="flex items-center gap-1.5 font-semibold text-sm">
                        <Truck className="w-4 h-4 shrink-0" />
                        אספקה מיידית — מכירה מהרכב
                      </span>
                      <span className="block text-xs mt-0.5 font-normal opacity-80">
                        {instantDelivery
                          ? 'תעודת משלוח תופק אוטומטית עם שליחת ההזמנה'
                          : 'הפק תעודת משלוח אוטומטית (Van Sale)'}
                      </span>
                    </span>

                    {instantDelivery && (
                      <FileCheck2 className="w-5 h-5 shrink-0 text-orange-600" />
                    )}
                  </button>
                )}

                <button
                  onClick={handleSubmitOrder}
                  disabled={submitting || !orderCtx}
                  className={[
                    'w-full py-3.5 rounded-2xl font-bold text-sm flex items-center justify-center',
                    'gap-2 transition-colors active:scale-[.98] disabled:cursor-not-allowed',
                    /* Phase 73: amber accent when the chooser shows consignment;
                       Van Sale orange takes priority when active (agents). */
                    instantDelivery && isAgent
                      ? 'bg-orange-500 hover:bg-orange-600 disabled:bg-orange-300 text-white'
                      : (cartIsConsignmentOnly() || (cartIsDualMode() && orderType === 'consignment'))
                        ? 'bg-amber-600 hover:bg-amber-700 disabled:bg-amber-300 text-white'
                        : 'bg-primary-600 hover:bg-primary-700 disabled:bg-primary-300 text-white',
                  ].join(' ')}
                >
                  {submitting ? (
                    <><Loader2 className="w-4 h-4 animate-spin" />שולח הזמנה…</>
                  ) : instantDelivery && isAgent ? (
                    <><Truck className="w-4 h-4" />שלח + הפק תעודת משלוח</>
                  ) : cartIsConsignmentOnly() ? (
                    <><Warehouse className="w-4 h-4" />שדר הזמנה (קונסגנציה)</>
                  ) : cartIsDualMode() && orderType === 'consignment' ? (
                    <><Warehouse className="w-4 h-4" />שדר הזמנה (קונסגנציה)</>
                  ) : (
                    <><ShoppingCart className="w-4 h-4" />שדר הזמנה</>
                  )}
                </button>

                <button
                  onClick={clearCart}
                  disabled={submitting}
                  className="w-full py-2.5 rounded-2xl border border-gray-200 text-sm text-gray-500
                             hover:bg-gray-50 disabled:opacity-40 transition-colors"
                >
                  נקה עגלה
                </button>
              </div>
            )}
          </div>
        </>
      )}

      {/* ── Expanded Cart (full-screen overlay) ──────────────────────── */}
      {cartOpen && cartExpanded && (
        <B2BExpandedCart
          items={items}
          totalItems={totalItems}
          totalPrice={totalPrice}
          setQuantity={setQuantity}
          removeItem={removeItem}
          clearCart={clearCart}
          onClose={() => { setCartOpen(false); setCartExpanded(false); }}
          onCollapse={() => setCartExpanded(false)}
          submitting={submitting}
          isAgent={isAgent}
          isClient={isClient}
          activeClientRecord={session?.activeClientRecord ? { name: session.activeClientRecord.name, company_id: session.activeClientRecord.company_id } : null}
          agentRecord={session?.agentRecord ? { name: session.agentRecord.name, email: session.agentRecord.email } : null}
          supplierNames={supplierNames}
          cartIsVatLiable={cartIsVatLiable}
          vatRate={vatRate}
          orderCtx={orderCtx}
          onSubmitOrder={handleSubmitOrder}
          handleSignOut={handleSignOut}
          cartIsDualMode={cartIsDualMode()}
          cartIsConsignmentOnly={cartIsConsignmentOnly()}
          orderType={orderType}
          setOrderType={setOrderType}
          instantDelivery={instantDelivery}
          setInstantDelivery={setInstantDelivery}
        />
      )}

      {/* ── Phase 73: Order Type Confirmation Modal ("Double Check") ─────────
          Rendered ONLY when at least one client targeted by the cart is
          consignment-eligible. Forces the user to make a conscious choice
          between standard / consignment immediately before the order is
          actually submitted — even if they already used the footer chooser.
          Closing the modal without clicking one of the two action buttons is
          intentionally NOT supported (no X / no overlay click) so the user
          cannot accidentally bypass this guard. */}
      {confirmModalOpen && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center
                     bg-black/50 backdrop-blur-sm p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="phase73-confirm-title"
        >
          <div className="bg-white rounded-3xl shadow-2xl w-full max-w-md
                          flex flex-col overflow-hidden">

            {/* Header */}
            <div className="flex items-start gap-3 px-6 pt-6 pb-3">
              <div className="p-2.5 rounded-2xl bg-amber-100 text-amber-700 shrink-0">
                <HelpCircle className="w-6 h-6" />
              </div>
              <div className="min-w-0">
                <h3 id="phase73-confirm-title"
                    className="text-lg font-bold text-gray-900 leading-tight">
                  האם זו הזמנת קונסגנציה?
                </h3>
                <p className="text-xs text-gray-500 mt-1 leading-snug">
                  הלקוח מאושר לרכישה בקונסגנציה. בחר את סוג ההזמנה לפני
                  השמירה — לא ניתן לשנות זאת לאחר השידור.
                </p>
              </div>
            </div>

            {/* Order total summary */}
            <div className="mx-6 mb-1 rounded-2xl bg-gray-50 border border-gray-100 px-4 py-3 space-y-1.5">
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-500">{cartIsVatLiable ? 'סה״כ לפני מע״מ' : 'סה״כ'}</span>
                <span className="font-semibold text-gray-900">₪{totalPrice.toFixed(2)}</span>
              </div>
              {cartIsVatLiable && vatRate != null && vatRate > 0 && (
                <>
                  <div className="flex items-center justify-between text-xs text-gray-400">
                    <span>מע״מ ({vatRate}%)</span>
                    <span>₪{(totalPrice * vatRate / 100).toFixed(2)}</span>
                  </div>
                  <div className="flex items-center justify-between text-sm font-bold text-primary-700 border-t border-gray-200 pt-1.5">
                    <span>סה״כ כולל מע״מ</span>
                    <span>₪{(totalPrice * (1 + vatRate / 100)).toFixed(2)}</span>
                  </div>
                </>
              )}
              {cartIsVatLiable && (vatRate == null || vatRate === 0) && (
                <div className="flex items-center justify-between text-xs text-gray-400">
                  <span>מע״מ</span>
                  <span>ייווסף בעת הפקת המסמך</span>
                </div>
              )}
              {!cartIsVatLiable && (
                <div className="flex items-center justify-between text-xs text-gray-400">
                  <span>פטור ממע״מ</span>
                  <span>—</span>
                </div>
              )}
            </div>

            {/* Action buttons */}
            <div className="px-6 pb-6 pt-2 grid grid-cols-1 sm:grid-cols-2 gap-3">
              <button
                type="button"
                onClick={handleConfirmConsignment}
                disabled={submitting}
                className="flex flex-col items-center justify-center gap-1.5 px-4 py-4
                           rounded-2xl bg-amber-600 hover:bg-amber-700 disabled:bg-amber-300
                           text-white font-bold text-sm transition-colors
                           active:scale-[.98] disabled:cursor-not-allowed"
              >
                <Warehouse className="w-5 h-5" />
                כן, זו קונסגנציה
              </button>
              <button
                type="button"
                onClick={handleConfirmStandard}
                disabled={submitting}
                className="flex flex-col items-center justify-center gap-1.5 px-4 py-4
                           rounded-2xl bg-primary-600 hover:bg-primary-700 disabled:bg-primary-300
                           text-white font-bold text-sm transition-colors
                           active:scale-[.98] disabled:cursor-not-allowed"
              >
                <Package className="w-5 h-5" />
                לא, רכישה רגילה
              </button>
            </div>

            {/* Cancel — keeps user in the cart so they can review/edit */}
            <div className="px-6 pb-5">
              <button
                type="button"
                onClick={() => setConfirmModalOpen(false)}
                disabled={submitting}
                className="w-full py-2.5 rounded-2xl border border-gray-200 text-sm
                           text-gray-500 hover:bg-gray-50 disabled:opacity-40
                           transition-colors"
              >
                ביטול
              </button>
            </div>

            {submitting && (
              <div className="border-t border-gray-100 px-6 py-3 flex items-center
                              justify-center gap-2 text-xs text-gray-500">
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
                שולח הזמנה…
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Public export: wraps Shell in cart provider ─────────────────────────────
// NOTE: B2BUserProvider is hoisted above both B2B routes in App.tsx so that
// a single context instance persists across navigate() calls between routes.

export function B2BLayout() {
  return (
    <B2BCartProvider>
      <B2BTermsProvider>
        <Shell />
      </B2BTermsProvider>
    </B2BCartProvider>
  );
}
